import { useState, useEffect } from "react";
import {
  Eye,
  AlertCircle,
  ShoppingCart,
  DollarSign,
  Smartphone,
  Download,
  BellRing,
  Copy,
  QrCode,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import clsx from "clsx";

/* ============== Saudação ============== */
function DashboardWelcomeMessage({ userName }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl font-bold mb-1">Olá, {userName}! 👋</h2>
      <p className="text-base text-slate-600 dark:text-slate-300">
        Bem-vindo ao SobeVenda. Acompanhe aqui o desempenho dos seus links e
        campanhas em tempo real!
      </p>
    </div>
  );
}

/* ============== Alertas ============== */
function DashboardAlert({ message, type = "warning" }) {
  const color =
    type === "warning"
      ? "bg-yellow-50 text-yellow-900 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-200 dark:border-yellow-900/40"
      : "bg-red-50 text-red-900 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-900/40";

  return (
    <div
      className={`flex items-center gap-2 w-full px-4 py-3 rounded-xl mb-6 border ${color}`}
    >
      <AlertCircle size={20} />
      <span>{message}</span>
    </div>
  );
}

/* ============== Banner dinâmico ============== */
function DashboardBanner({ banner, onClose }) {
  if (!banner) return null;

  const {
    title,
    subtitle,
    cta_label,
    cta_url,
    image_url,
    bg_color,
    text_color,
  } = banner;

  const bgColorMap = {
    "bg-gray-100": "bg-gray-100",
    "bg-slate-700": "bg-slate-700",
    "bg-slate-800": "bg-slate-800",
    "bg-indigo-600": "bg-indigo-600",
    "bg-amber-100": "bg-amber-100",
    "bg-red-100": "bg-red-100",
    "bg-emerald-100": "bg-emerald-100",
    "bg-zinc-900": "bg-zinc-900",
    "bg-emerald-900": "bg-emerald-900",
    "bg-teal-900": "bg-teal-900",
  };

  const textColorMap = {
    "text-white": "text-white",
    "text-black": "text-black",
    "text-slate-900": "text-slate-900",
    "text-amber-200": "text-amber-200",
    "text-amber-400": "text-amber-400",
    "text-slate-100": "text-slate-100",
    "text-emerald-50": "text-emerald-50",
    "text-teal-50": "text-teal-50",
  };

  return (
    <div
      className={clsx(
        "mb-6 rounded-2xl p-4 md:p-5 flex items-center gap-4 border border-[rgb(var(--border))]",
        "shadow-[0_1px_0_rgba(0,0,0,0.03),0_10px_20px_-10px_rgba(0,0,0,0.10)]",
        bgColorMap[bg_color] || "bg-indigo-600",
        textColorMap[text_color] || "text-white"
      )}
    >
      <div className="flex-1 min-w-0">
        <h3 className="text-base md:text-lg font-semibold mb-1 line-clamp-2">
          {title}
        </h3>

        {subtitle && (
          <p className="text-sm md:text-[13px] opacity-90 line-clamp-3">
            {subtitle}
          </p>
        )}

        {cta_url && cta_label && (
          <a
            href={cta_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center mt-3 px-3 py-1.5 rounded-md text-xs font-semibold bg-white/95 text-slate-900 hover:bg-white"
          >
            {cta_label}
          </a>
        )}
      </div>

      {image_url && (
        <div className="hidden sm:block">
          <img
            src={image_url}
            alt={title}
            className="h-16 md:h-20 w-auto rounded-lg object-cover shadow"
          />
        </div>
      )}

      <button
        onClick={onClose}
        className="self-start ml-2 text-xs opacity-80 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}

/* ============== Badge estilo App Store ============== */
function AppStoreBadge() {
  return (
    <div
      className="
        inline-flex items-center gap-3
        rounded-2xl border border-white/10 bg-zinc-950
        px-4 py-3 text-white shadow-md
      "
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
        <Smartphone size={20} />
      </div>

      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">
          Baixe na
        </div>
        <div className="text-sm font-semibold">App Store</div>
      </div>
    </div>
  );
}

/* ============== Card App Mobile ============== */
function AppDownloadCard() {
  const appStoreUrl = "https://apps.apple.com/app/sobevenda/id6758171479";
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
    appStoreUrl
  )}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(appStoreUrl);
      alert("Link da App Store copiado!");
    } catch (error) {
      console.error("Erro ao copiar link:", error);
      alert("Não foi possível copiar o link.");
    }
  };

  return (
    <div
      className="
        mb-6 rounded-3xl p-5 md:p-6
        bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950
        border border-slate-800
        shadow-[0_1px_0_rgba(255,255,255,0.02),0_12px_30px_-12px_rgba(0,0,0,0.45)]
        overflow-hidden
      "
    >
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              className="
                inline-flex items-center gap-2
                rounded-full border border-emerald-400/20
                bg-emerald-400/10 px-3 py-1
                text-xs font-semibold text-emerald-300
              "
            >
              <BellRing size={14} />
              Novo app disponível
            </span>

            <span
              className="
                inline-flex items-center gap-2
                rounded-full border border-blue-400/20
                bg-blue-400/10 px-3 py-1
                text-xs font-semibold text-blue-200
              "
            >
              <Smartphone size={14} />
              iPhone
            </span>
          </div>

          <h3 className="text-2xl md:text-[28px] font-bold text-white leading-tight">
            Baixe o app do SobeVenda
          </h3>

          <p className="mt-3 max-w-2xl text-sm md:text-base text-slate-300 leading-relaxed">
            Acompanhe suas vendas e receba notificações em tempo real direto no
            celular. Tenha acesso rápido ao seu desempenho onde estiver.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 border border-white/10">
              <BellRing size={14} />
              Alertas em tempo real
            </span>

            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 border border-white/10">
              <QrCode size={14} />
              QR Code para baixar
            </span>

            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 border border-white/10">
              <Smartphone size={14} />
              Acesso rápido no iPhone
            </span>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noreferrer"
              className="
                inline-flex items-center justify-center gap-2
                rounded-2xl bg-blue-600 px-4 py-3
                text-sm font-semibold text-white
                transition hover:bg-blue-500
              "
            >
              <Download size={16} />
              Baixar na App Store
            </a>

            <button
              onClick={handleCopy}
              className="
                inline-flex items-center justify-center gap-2
                rounded-2xl border border-white/10 bg-white/5
                px-4 py-3 text-sm font-semibold text-slate-200
                transition hover:bg-white/10
              "
            >
              <Copy size={16} />
              Copiar link
            </button>
          </div>

          <div className="mt-5">
            <AppStoreBadge />
          </div>
        </div>

        <div className="xl:w-[250px] shrink-0">
          <div
            className="
              rounded-3xl border border-white/10 bg-white/5
              p-4 backdrop-blur-sm
            "
          >
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
              Escaneie para baixar
            </div>

            <div className="mx-auto w-full max-w-[280px] rounded-2xl bg-white p-3 shadow-inner">
  <img
    src={qrCodeUrl}
    alt="QR Code para baixar o app do SobeVenda"
    className="mx-auto w-full max-w-[240px] h-auto rounded-xl"
  />
</div>

            <p className="mt-3 text-xs leading-relaxed text-slate-300">
              Abra a câmera do iPhone e escaneie o QR Code para acessar
              diretamente a App Store.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============== Página ============== */
export default function DashboardPrincipal({ alert }) {
  const [userName, setUserName] = useState("Usuário");
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    const DASHBOARD_URL =
      "https://api.sobevenda.com.br/api-proxy/affiliate/dashboard-data";

    const AUTH = {
      Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}`,
    };

    async function fetchData() {
      try {
        const res = await axios.get(DASHBOARD_URL, { headers: AUTH });
        const data = res.data || {};

        setUserName(data.config?.name || "Usuário");
        setScripts(Array.isArray(data.scripts) ? data.scripts : []);

        if (data.banner && !Array.isArray(data.banner)) {
          setBanner(data.banner);
        } else if (Array.isArray(data.banner) && data.banner.length > 0) {
          setBanner(data.banner[0]);
        } else {
          setBanner(null);
        }
      } catch (err) {
        setScripts([]);
        setBanner(null);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const primaryBtn = "bg-blue-600 hover:bg-blue-500 text-white";
  const topScript = scripts[0] || null;

  return (
    <div className="p-6 max-w-6xl text-[rgb(var(--fg))] bg-[rgb(var(--bg))]">
      <DashboardWelcomeMessage userName={userName} />

      {banner && !bannerDismissed && (
        <DashboardBanner
          banner={banner}
          onClose={() => setBannerDismissed(true)}
        />
      )}

      {alert && <DashboardAlert message={alert.message} type={alert.type} />}

      <AppDownloadCard />

      
    </div>
  );
}